import {useCallback} from 'react';
import {Problem} from '@synako/halitrek';
import {
  getAddArticlePayloadWithColisFallback,
  isItemStateCatalogueFF,
  useLivraisonsPossiblesContext,
} from 'ff-common';
import {useClient} from '@/haligator-factory';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useAjouterAuPanier} from '@/hooks/livraison/use-ajouter-au-panier';

/**
 * Ajoute un article au panier depuis une modale / un side panel de sélection
 * (produits saisonniers, "Voir tous les produits de l'UB"…) à partir d'un simple
 * code article : résout l'item du catalogue, calcule la quantité valide
 * (multiple/minimum de commande), puis délègue l'ajout à `useAjouterAuPanier`
 * (toast + lien panier + contrôle de disponibilité).
 */
export const useAjouterAuPanierDepuisModale = () => {
  const client = useClient();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const {livraisonSelectionnee} = useLivraisonsPossiblesContext();
  const ajouterAuPanier = useAjouterAuPanier(
    livraisonSelectionnee.data.id,
    textToast,
  );

  return useCallback(
    async (code_article: string) => {
      const itemCatalogue = await client
        .follow('go:item_catalogue_2', {
          marketplace_id: 'FF',
          code: code_article,
        })
        .get()
        .catch((error: unknown) => {
          onError(error as Problem);
          return undefined;
        });
      if (!itemCatalogue || !isItemStateCatalogueFF(itemCatalogue)) return;
      // Ajout en unitaire (pas de colis dans le panel). La quantité doit
      // être un multiple de `multiple_de_commande_client` côté serveur.
      const payload = getAddArticlePayloadWithColisFallback({
        commandeEnColis: false,
        quantiteColisEntier: itemCatalogue.data.quantite_colis_entier,
        minimumCommande: itemCatalogue.data.minimum_de_commande_client,
        multipleCommande: itemCatalogue.data.multiple_de_commande_client,
      });

      ajouterAuPanier.submit({code_article, ...payload});
    },
    [client, onError, ajouterAuPanier],
  );
};
